
import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { TourDetailHeader } from '@/components/tour/TourDetailHeader';
import { TourDescription } from '@/components/tour/TourDescription';
import { TourExhibitsList } from '@/components/tour/TourExhibitsList';

import { TourMapSection } from '@/components/tour/TourMapSection';
import { TourDetailSkeleton } from '@/components/tour/TourDetailSkeleton';
import { TourDetailError } from '@/components/tour/TourDetailError';
import { useTourDetail } from '@/hooks/useTourDetail';

const TourDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { trackTourView } = useAnalyticsTracking();
  
  // Check if we're coming from the CMS
  const fromCMS = location.state?.from === 'cms';
  
  // Use our custom hook to fetch tour data
  const { tour, tourExhibits, loading, error } = useTourDetail(id);

  // Track tour view
  useEffect(() => {
    if (tour && id && !fromCMS) {
      trackTourView(id);
    }
  }, [tour, id, fromCMS, trackTourView]);
  
  // Loading state
  if (loading) {
    return <TourDetailSkeleton />;
  }
  
  // If tour not found, display error
  if (error || !tour) {
    return <TourDetailError />;
  }
  
  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8 relative">
      {/* Abstract shapes in background */}
      <div className="agam-shape agam-shape-1"></div>
      <div className="agam-shape agam-shape-2"></div>
      <div className="agam-shape agam-shape-3"></div>
      
      {/* Tour header with image and title */}
      <TourDetailHeader tour={tour} />
      
      {/* Tour description */}
      <TourDescription tour={tour} />
      
      {/* Tour Map Section - only shows if map is available */}
      <TourMapSection tour={{
        id: tour.id,
        mapType: (tour.mapType === 'indoor' || tour.mapType === 'outdoor') ? tour.mapType : 'indoor',
        mapImage: tour.mapImage
      }} />
      
      {/* Tour exhibits list */}
      <TourExhibitsList 
        tourId={tour.id}
        exhibits={tourExhibits}
      />
      
    </div>
  );
};

export default TourDetail;
